import { IOption } from '../../../../components/SearchMultiselect'
import {
  IFiltersParams,
  TCheckboxFiltersParams,
  TSelectFiltersParams,
} from '../SearchQuestionsContent/types'

export const INITIAL_SELECTED_DATA = {
  subjects: [],
  themes: [],
  topics: [],
  years: [],
  evaluatingInstitutions: [],
  schooling: [],
  publicTenders: [],
  questionType: [],
  roles: [],
  graduation: [],
}

export const INITIAL_CHECKBOXES_DATA = {
  canceled: false,
  commented: false,
}

export const INITIAL_MY_QUESTIONS_DATA = {
  correct: false,
  wrong: false,
  unanswered: false,
}

export const CheckboxTexts: Record<
  keyof typeof INITIAL_CHECKBOXES_DATA,
  { label: string; value: string }
> = {
  canceled: { label: 'Exibir questões', value: 'Anuladas' },
  commented: { label: 'Exibir questões', value: 'Comentadas' },
}

export const SelectLabels: Record<keyof typeof INITIAL_SELECTED_DATA, string> = {
  subjects: 'Disciplinas',
  themes: 'Assuntos',
  years: 'Ano',
  evaluatingInstitutions: 'Banca',
  topics: 'Tópicos',
  publicTenders: 'Órgão',
  roles: 'Cargo',
  schooling: 'Escolaridade',
  graduation: 'Área de formação',
  questionType: 'Tipo de questão',
}

export const MyQuestionsLabels: Record<keyof typeof INITIAL_MY_QUESTIONS_DATA, string> = {
  correct: 'Corretas',
  wrong: 'Erradas',
  unanswered: 'Não respondidas',
}

export type TSelected = Record<keyof typeof INITIAL_SELECTED_DATA, IOption[]>
export type TSelectName = keyof TSelected

export type TCheckboxesData = typeof INITIAL_CHECKBOXES_DATA
export type TCheckboxName = keyof TCheckboxesData

export type TMyQuestions = typeof INITIAL_MY_QUESTIONS_DATA

export const createOption = <T extends Record<string, any>>(
  filtersData: T,
  key: keyof T
): IOption[] => {
  const values = filtersData[key]

  if (values.length === 0) return []

  const options = values.map((value) => {
    const option: IOption = {
      value: value?.code || value,
      label: value?.abbreviation || value?.name || value,
    }

    if (key === 'publicTenders' || key === 'evaluatingInstitutions') {
      option.customSearchKey = value?.name
      option.title = value?.name
    }

    return option
  })

  if (key === 'years') options.sort((a, b) => Number(b.value) - Number(a.value))

  return options
}

export const selectsToParams = (
  selected: TSelected
): { params: TSelectFiltersParams; count: number } => {
  let count = 0

  const mapToValue = (name: keyof TSelected) =>
    selected[name].map((option) => {
      count++
      return option.value
    })

  return {
    params: {
      subjects: mapToValue('subjects') as string[],
      themes: mapToValue('themes') as string[],
      topics: mapToValue('topics') as string[],
      years: mapToValue('years') as number[],
      evaluatingInstitutions: mapToValue('evaluatingInstitutions') as string[],
      schooling: mapToValue('schooling') as string[],
      publicTenders: mapToValue('publicTenders') as string[],
      questionType: mapToValue('questionType') as TSelectFiltersParams['questionType'],
      roles: mapToValue('roles') as string[],
      graduation: mapToValue('graduation') as string[],
    },
    count,
  }
}

export const checkboxesToParams = (
  checkboxes: TCheckboxesData
): { params: TCheckboxFiltersParams; count: number } => {
  const includes = (() => {
    const arr: IFiltersParams['includes'] = []

    if (checkboxes.canceled === false) {
      arr.push({ canceled: false })
    }

    if (checkboxes.commented === true) {
      arr.push({ commented: true })
    }

    return arr
  })()

  const count = Object.values(checkboxes).reduce(
    (acc, value) => (value === true ? acc + 1 : acc),
    0
  )

  return {
    params: {
      includes,
    },
    count,
  }
}

export const myQuestionsToParam = (
  myQuestions: TMyQuestions
): { param: IFiltersParams['myQuestions']; count: number } => {
  const param = []

  Object.entries(myQuestions).forEach(([name, value]) => value && param.push(name))

  return {
    param,
    count: param.length,
  }
}
